import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  SegmentedControl,
  Textarea,
} from '@mlm/ui';
import {
  createExpense,
  createExpenseCategory,
  fetchReport,
  listExpenseCategories,
  listExpenses,
  listFundsAccounts,
  listGlAccounts,
  listSuppliers,
  updateExpenseCategory,
  voidExpense,
  type AcctDocStatus,
  type ExpenseCategory,
  type ExpenseRow,
} from '../../lib/api';
import { defaultFundsAccountFor } from '@mlm/shared';
import { downloadReport, type ReportExportMeta } from '../../lib/report-export';
import { ReportExportButtons } from '../../components/report-export-buttons';
import { useHasPerm } from '../../lib/auth-store';

/**
 * Accounting v1 — LIVE expenses workspace (QuickBooks Expenses feel).
 * Reads gate on `view.accounting` (router-level); the write surface (record /
 * void / categories) hides without `expense.write` and the server enforces it.
 */

const PAYMENT_SOURCES = ['cash', 'bank', 'gcash', 'check', 'other'] as const;
const SOURCE_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank: 'Bank',
  gcash: 'GCash',
  check: 'Check',
  other: 'Other',
};

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/** Server error slugs a human shouldn't have to decode. */
function friendlyErr(error: unknown): string | undefined {
  const code = errText(error);
  if (!code) return undefined;
  if (code === 'category_name_taken') return 'That category name is already taken.';
  if (code === 'gl_account_unknown') return 'That account is no longer in the chart of accounts.';
  if (code === 'gl_account_not_expense') return 'Pick an expense-type account.';
  if (code === 'gl_account_inactive') return 'That account is deactivated.';
  return code;
}

/**
 * ₱ display formatter. Money arrives as decimal STRINGS; `Number` here is
 * display-only formatting, never math that feeds back to the server.
 */
function peso(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v);
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function localIsoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function monthStartIso(): string {
  const d = new Date();
  return localIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

/**
 * The reports API wants full ISO datetimes (`z.string().datetime()` — a bare
 * `YYYY-MM-DD` is a 400), and a date-only string would parse as UTC midnight
 * anyway, cutting the "to" day short in Manila. Send local day bounds.
 */
function windowParams(from: string, to: string): { from?: string; to?: string } {
  return {
    ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
  };
}

/** Display-side VAT prefill: gross × 12/112, rounded to 2dp. */
function vatFromGross(amount: string): string {
  const n = Number(amount);
  if (!amount || !Number.isFinite(n) || n <= 0) return '';
  return ((n * 12) / 112).toFixed(2);
}

const selectCls = 'h-10 rounded-md border border-input bg-background px-3 text-sm';

export function ExpensesPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('expense.write');

  // Filters — default to the current month.
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(localIsoDate(new Date()));
  const [categoryId, setCategoryId] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState<'all' | AcctDocStatus>('all');
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  // Tiny debounce instead of pulling in lodash (members.tsx pattern).
  if (q !== qDebounced) {
    setTimeout(() => setQDebounced(q), 250);
  }

  const [newOpen, setNewOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState<ExpenseRow | null>(null);

  const range = windowParams(from, to);
  const filter = {
    ...range,
    categoryId: categoryId || undefined,
    paymentSource: source || undefined,
    status: status === 'all' ? undefined : status,
    q: qDebounced.trim() || undefined,
  };
  const expensesQ = useQuery({
    queryKey: ['expenses', filter],
    queryFn: () => listExpenses(filter),
  });
  const categoriesQ = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => listExpenseCategories(),
  });
  const byCatQ = useQuery({
    queryKey: ['report', 'expense-by-category', from, to],
    queryFn: () => fetchReport('expense-by-category', range),
  });
  const byCatMeta: ReportExportMeta = {
    title: 'Expenses by Category',
    sheetName: 'By category',
    subtitle: [`Period: ${from} → ${to}`],
  };

  const rows = expensesQ.data ?? [];
  const categories = categoriesQ.data ?? [];
  // Display-only Number math (summary footer), never sent back to the server.
  const postedRows = rows.filter((r) => r.status === 'posted');
  const postedTotal = postedRows.reduce((s, r) => s + Number(r.amount), 0);
  const postedVat = postedRows.reduce((s, r) => s + Number(r.vatAmount), 0);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['report', 'expense-by-category'] });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Expenses</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {expensesQ.isLoading
              ? 'Loading…'
              : `${rows.length} expense${rows.length === 1 ? '' : 's'} in range · ${peso(postedTotal)} posted`}
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCatsOpen(true)}>
              Manage categories
            </Button>
            <Button onClick={() => setNewOpen(true)}>+ New Expense</Button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-2 pt-4">
          <div>
            <p className="mb-1 text-xs text-muted-foreground">From</p>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">To</p>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Category</p>
            <select
              className={selectCls}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {!c.active && ' · inactive'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Source</p>
            <select
              className={selectCls}
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="">All sources</option>
              {PAYMENT_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Status</p>
            <select
              className={selectCls}
              value={status}
              onChange={(e) => setStatus(e.target.value as 'all' | AcctDocStatus)}
            >
              <option value="all">All</option>
              <option value="posted">Posted</option>
              <option value="void">Voided</option>
            </select>
          </div>
          <div className="min-w-[200px] flex-1">
            <p className="mb-1 text-xs text-muted-foreground">Search</p>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Payee, doc #, reference…"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              setFrom(monthStartIso());
              setTo(localIsoDate(new Date()));
              setCategoryId('');
              setSource('');
              setStatus('all');
              setQ('');
              setQDebounced('');
            }}
          >
            Reset
          </Button>
        </CardContent>
      </Card>

      <div className="grid items-start gap-4 xl:grid-cols-[1fr_320px]">
        {/* Expense list */}
        <Card>
          <CardHeader>
            <CardTitle>
              Expenses{' '}
              <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {expensesQ.isLoading ? (
              <p className="p-6 text-sm text-muted-foreground">Loading…</p>
            ) : expensesQ.error ? (
              <p className="p-6 text-sm text-destructive">
                {errText(expensesQ.error) ?? 'Failed to load expenses.'}
              </p>
            ) : rows.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">No expenses in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">EXP #</th>
                      <th className="px-4 py-2">Payee</th>
                      <th className="px-4 py-2">Category</th>
                      <th className="px-4 py-2">Source</th>
                      <th className="px-4 py-2">Ref</th>
                      <th className="px-4 py-2 text-right">VAT</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2">Status</th>
                      {canWrite && <th className="px-4 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const voided = r.status === 'void';
                      return (
                        <tr
                          key={r.id}
                          className={
                            'border-b last:border-0 hover:bg-muted/30 ' +
                            (voided ? 'text-muted-foreground opacity-60' : '')
                          }
                          title={voided && r.voidReason ? `Voided: ${r.voidReason}` : undefined}
                        >
                          <td className="px-4 py-2 text-xs text-muted-foreground">
                            {new Date(r.expenseDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                          <td className="px-4 py-2">{r.supplierName ?? r.payeeName ?? '—'}</td>
                          <td className="px-4 py-2 text-xs">{r.categoryName}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">
                            {SOURCE_LABELS[r.paymentSource] ?? r.paymentSource}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                            {r.reference ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">
                            {Number(r.vatAmount) > 0 ? peso(r.vatAmount) : '—'}
                          </td>
                          <td className="px-4 py-2 text-right font-mono tabular-nums">
                            {peso(r.amount)}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={voided ? 'muted' : 'success'}>{r.status}</Badge>
                          </td>
                          {canWrite && (
                            <td className="px-4 py-2 text-right">
                              {!voided && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setVoidTarget(r)}
                                >
                                  Void
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 font-semibold">
                      <td className="px-4 py-2" colSpan={6}>
                        Total posted (listed)
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">
                        {postedVat > 0 ? peso(postedVat) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(postedTotal)}
                      </td>
                      <td colSpan={canWrite ? 2 : 1} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By-category side card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">By category</CardTitle>
            <ReportExportButtons
              result={byCatQ.data}
              meta={byCatMeta}
              onCsv={() =>
                downloadReport('expense-by-category', range, 'csv', {
                  ...byCatMeta,
                  result: byCatQ.data,
                })
              }
            />
          </CardHeader>
          <CardContent className="p-0">
            {byCatQ.isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Loading…</p>
            ) : byCatQ.error ? (
              <p className="p-4 text-sm text-destructive">Failed to load the breakdown.</p>
            ) : !byCatQ.data?.rows.length ? (
              <p className="p-4 text-sm text-muted-foreground">No posted expenses in range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      {byCatQ.data.columns.map((c) => (
                        <th
                          key={c.key}
                          className={
                            'px-4 py-2 font-medium ' + (c.align === 'right' ? 'text-right' : '')
                          }
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byCatQ.data.rows.map((row, i) => (
                      <tr key={i} className="border-b last:border-0">
                        {byCatQ.data.columns.map((c) => (
                          <td
                            key={c.key}
                            className={
                              'px-4 py-1.5 text-xs ' +
                              (c.align === 'right' ? 'text-right font-mono tabular-nums' : '')
                            }
                          >
                            {row[c.key] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {byCatQ.data.summary?.length ? (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 border-t px-4 py-3 text-xs">
                    {byCatQ.data.summary.map((s) => (
                      <span key={s.label}>
                        <span className="text-muted-foreground">{s.label}: </span>
                        <span className="font-medium tabular-nums">{s.value}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {newOpen && (
        <NewExpenseDialog
          categories={categories.filter((c) => c.active)}
          onClose={() => setNewOpen(false)}
          onCreated={() => {
            refresh();
            setNewOpen(false);
          }}
        />
      )}
      {catsOpen && (
        <CategoriesDialog
          categories={categories}
          loading={categoriesQ.isLoading}
          onClose={() => setCatsOpen(false)}
        />
      )}
      {voidTarget && (
        <VoidExpenseDialog
          expense={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={() => {
            refresh();
            setVoidTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Expense dialog
// ---------------------------------------------------------------------------

function NewExpenseDialog({
  categories,
  onClose,
  onCreated,
}: {
  categories: ExpenseCategory[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => listSuppliers(true),
  });

  const [date, setDate] = useState(localIsoDate(new Date()));
  const [payeeMode, setPayeeMode] = useState<'freeform' | 'supplier'>('freeform');
  const [payeeName, setPayeeName] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [catId, setCatId] = useState('');
  const [amount, setAmount] = useState('');
  const [vatOn, setVatOn] = useState(false);
  const [vat, setVat] = useState('');
  const [vatDirty, setVatDirty] = useState(false);
  const [paymentSource, setPaymentSource] = useState<string>('cash');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');

  // Cash/Bank register — the default tracks the payment source until the user
  // chooses for themselves.
  const fundsQ = useQuery({
    queryKey: ['funds-accounts', 'active'],
    queryFn: () => listFundsAccounts(true),
  });
  const fundsAccounts = fundsQ.data?.rows ?? [];
  const fundsRequired = fundsQ.data?.required ?? false;
  const [fundsTouched, setFundsTouched] = useState(false);
  const [fundsAccountId, setFundsAccountId] = useState('');
  const suggestedFunds =
    defaultFundsAccountFor(fundsAccounts, { method: paymentSource, branchId: null }) ?? '';
  const effectiveFundsAccountId = fundsTouched ? fundsAccountId : suggestedFunds;

  const create = useMutation({
    mutationFn: createExpense,
    meta: { successMessage: 'Expense recorded.' },
    onSuccess: onCreated,
  });

  const canSubmit =
    !!date &&
    !!catId &&
    !!amount &&
    (payeeMode === 'freeform' ? payeeName.trim().length > 0 : supplierId.length > 0) &&
    (!fundsRequired || !!effectiveFundsAccountId);

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !create.isPending) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New expense</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit || create.isPending) return;
            create.mutate({
              expenseDate: date,
              payeeName: payeeMode === 'freeform' ? payeeName.trim() : undefined,
              supplierId: payeeMode === 'supplier' ? supplierId : undefined,
              categoryId: catId,
              amount,
              vatAmount: vatOn && vat ? vat : null,
              paymentSource,
              reference: reference.trim() || null,
              memo: memo.trim() || null,
              fundsAccountId: effectiveFundsAccountId || null,
              // A branch-bound till can only take a document from its OWN
              // branch (`funds_account_branch_required`), so paying an expense
              // out of one attributes the expense to that branch.
              branchId:
                fundsAccounts.find((a) => a.id === effectiveFundsAccountId)?.branchId ?? null,
            });
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
            {(fundsAccounts.length > 0 || fundsRequired) && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Paid from{fundsRequired ? '' : ' (optional)'}
                </label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={effectiveFundsAccountId}
                  onChange={(e) => {
                    setFundsTouched(true);
                    setFundsAccountId(e.target.value);
                  }}
                >
                  <option value="">— none —</option>
                  {fundsAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Payment source</label>
              <select
                className={selectCls + ' w-full'}
                value={paymentSource}
                onChange={(e) => setPaymentSource(e.target.value)}
              >
                {PAYMENT_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Paid to</label>
              <SegmentedControl
                size="sm"
                ariaLabel="Payee type"
                value={payeeMode}
                onChange={(v) => setPayeeMode(v)}
                options={[
                  { value: 'freeform', label: 'Payee name' },
                  { value: 'supplier', label: 'Supplier' },
                ]}
              />
            </div>
            {payeeMode === 'freeform' ? (
              <Input
                value={payeeName}
                onChange={(e) => setPayeeName(e.target.value)}
                placeholder="Who was paid — e.g. Meralco, LBC…"
                required
              />
            ) : (
              <select
                className={selectCls + ' w-full'}
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">
                  {suppliersQ.isLoading ? 'Loading suppliers…' : 'Select a supplier…'}
                </option>
                {(suppliersQ.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Category</label>
              <select
                className={selectCls + ' w-full'}
                value={catId}
                onChange={(e) => setCatId(e.target.value)}
                required
              >
                <option value="">Select a category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Amount (₱)</label>
              <MoneyInput
                value={amount}
                onChange={(e) => {
                  const v = e.target.value;
                  setAmount(v);
                  if (vatOn && !vatDirty) setVat(vatFromGross(v));
                }}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={vatOn}
                onCheckedChange={(v) => {
                  const on = v === true;
                  setVatOn(on);
                  if (on) {
                    setVat(vatFromGross(amount));
                    setVatDirty(false);
                  }
                }}
              />
              VAT included
            </label>
            {vatOn && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">VAT amount (₱)</label>
                <MoneyInput
                  value={vat}
                  onChange={(e) => {
                    setVat(e.target.value);
                    setVatDirty(true);
                  }}
                  placeholder="0.00"
                />
                <p className="text-[11px] text-muted-foreground">
                  Prefilled at 12/112 of the amount — adjust if the receipt says otherwise.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Reference</label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="OR / invoice no., txn ref…"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-muted-foreground">Memo</label>
              <Textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                placeholder="What this was for (optional)"
              />
            </div>
          </div>

          {friendlyErr(create.error) && (
            <p className="text-sm text-destructive">{friendlyErr(create.error)}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || create.isPending}>
              {create.isPending ? 'Saving…' : 'Record expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Void dialog
// ---------------------------------------------------------------------------

function VoidExpenseDialog({
  expense,
  onClose,
  onVoided,
}: {
  expense: ExpenseRow;
  onClose: () => void;
  onVoided: () => void;
}) {
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => voidExpense(expense.id, reason.trim()),
    meta: { successMessage: 'Expense voided.' },
    onSuccess: onVoided,
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !mut.isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Void expense {expense.docNo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {peso(expense.amount)} to{' '}
            <span className="font-medium text-foreground">
              {expense.supplierName ?? expense.payeeName ?? '—'}
            </span>{' '}
            ({expense.categoryName}). Voiding removes it from totals and reports; the row stays for
            the audit trail.
          </p>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Reason</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why this is being voided…"
            />
          </div>
          {friendlyErr(mut.error) && (
            <p className="text-sm text-destructive">{friendlyErr(mut.error)}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? 'Voiding…' : 'Void expense'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Manage categories dialog
// ---------------------------------------------------------------------------

function CategoriesDialog({
  categories,
  loading,
  onClose,
}: {
  categories: ExpenseCategory[];
  loading: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');

  // Only expense-type accounts may back a category: the Income Statement
  // classifies by `type`, so a category pointed at a liability would misfile
  // every expense booked through it. The server enforces the same rule.
  const glAccountsQ = useQuery({ queryKey: ['gl-accounts'], queryFn: listGlAccounts });
  const accountOptions = (glAccountsQ.data?.rows ?? []).filter(
    (a) => a.active && a.type === 'expense',
  );
  const accountLabel = (code: string | null) => {
    if (!code) return null;
    const match = accountOptions.find((a) => a.code === code);
    return match ? `${match.code} — ${match.name}` : code;
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['expense-categories'] });
    // Category names are denormalised onto expense rows.
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['report', 'expense-by-category'] });
  };

  const catErrorCodes = [
    'category_name_taken',
    'gl_account_unknown',
    'gl_account_not_expense',
    'gl_account_inactive',
  ];

  const createCat = useMutation({
    mutationFn: (input: { name: string; glAccountCode: string | null }) =>
      createExpenseCategory(input),
    meta: { successMessage: 'Category added.', silentErrorCodes: catErrorCodes },
    onSuccess: () => {
      invalidate();
      setNewName('');
      setNewCode('');
    },
  });
  const updateCat = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { name?: string; active?: boolean; glAccountCode?: string | null };
    }) => updateExpenseCategory(id, patch),
    meta: { successMessage: 'Category saved.', silentErrorCodes: catErrorCodes },
    onSuccess: () => {
      invalidate();
      setEditingId(null);
    },
  });

  const sorted = [...categories].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !createCat.isPending && !updateCat.isPending) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Expense categories</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">No categories yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {sorted.map((c) => (
                <li key={c.id} className="flex items-center gap-2 px-3 py-2">
                  {editingId === c.id ? (
                    <form
                      className="flex-1 space-y-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const name = editName.trim();
                        if (!name || updateCat.isPending) return;
                        updateCat.mutate({
                          id: c.id,
                          patch: { name, glAccountCode: editCode || null },
                        });
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8"
                          autoFocus
                          required
                        />
                        <Button type="submit" size="sm" disabled={updateCat.isPending}>
                          Save
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <select
                        value={editCode}
                        onChange={(e) => setEditCode(e.target.value)}
                        className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                      >
                        <option value="">Unmapped — books to 6900</option>
                        {accountOptions.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name}
                          </option>
                        ))}
                      </select>
                    </form>
                  ) : (
                    <>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className={
                              'truncate text-sm ' +
                              (c.active ? '' : 'text-muted-foreground line-through')
                            }
                          >
                            {c.name}
                          </span>
                          {c.isSystem && <Badge variant="secondary">System</Badge>}
                        </div>
                        <span className="truncate text-xs text-muted-foreground">
                          {accountLabel(c.glAccountCode) ?? 'Unmapped — books to 6900'}
                        </span>
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Checkbox
                          checked={c.active}
                          disabled={updateCat.isPending}
                          onCheckedChange={(v) =>
                            updateCat.mutate({ id: c.id, patch: { active: v === true } })
                          }
                        />
                        Active
                      </label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(c.id);
                          setEditName(c.name);
                          setEditCode(c.glAccountCode ?? '');
                        }}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          <form
            className="space-y-2 rounded-md border p-3"
            onSubmit={(e) => {
              e.preventDefault();
              const name = newName.trim();
              if (!name || createCat.isPending) return;
              createCat.mutate({ name, glAccountCode: newCode || null });
            }}
          >
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New category name…"
                required
              />
              <Button type="submit" disabled={!newName.trim() || createCat.isPending}>
                {createCat.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
            <select
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">Unmapped — books to 6900</option>
              {accountOptions.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Pick the expense account this category posts to. Left unmapped, its expenses land in
              6900 Misc / Unmapped. Changing it only affects future postings.
            </p>
          </form>

          {friendlyErr(createCat.error) && (
            <p className="text-sm text-destructive">{friendlyErr(createCat.error)}</p>
          )}
          {friendlyErr(updateCat.error) && (
            <p className="text-sm text-destructive">{friendlyErr(updateCat.error)}</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
